/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import LinkButton from 'flarum/common/components/LinkButton';
import LogInButtons from 'flarum/forum/components/LogInButtons';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import LogInModal from 'flarum/forum/components/LogInModal';
import UserSecurityPage from 'flarum/forum/components/UserSecurityPage';
import SettingsPage from 'flarum/forum/components/SettingsPage';
import LinkedAccounts from '../components/LinkedAccounts';
import Application from 'flarum/common/Application';
import RequestError from 'flarum/common/utils/RequestError';
import type Mithril from 'mithril';

/**
 * Whether an `ItemList<Mithril.Children>` entry is a JSX vnode — the only kind carrying `attrs`.
 */
function isVnode(child: Mithril.Children): child is Mithril.Vnode<Record<string, unknown>> {
	return typeof child === 'object' && child !== null && !Array.isArray(child);
}

export default function () {
	extend(LogInButtons.prototype, 'items', (items) => {
		const brandName = app.forum.attribute<string>('brandName');
		items.add('of-login-phrase', <p>{app.translator.trans('glowingblue-orell-thalia-common.forum.login.with_of', { brandName })}:</p>, 201);
	});

	extend(SignUpModal.prototype, 'fields', function (items) {
		if (!!this.attrs.token && !!!this.attrs.username) {
			items.setContent(
				'username-help',
				<div>
					<p>
						{app.translator.trans('glowingblue-orell-thalia-common.forum.login.needs_username', {
							forum: app.forum.attribute<string>('title'),
						})}
					</p>
				</div>
			);
		} else {
			items.add('separating-phrase', <p>{app.translator.trans('glowingblue-orell-thalia-common.forum.login.separating_phrase_signup')}</p>, 100);
		}
	});
	extend(LogInModal.prototype, 'fields', (items) => {
		const communityName = app.forum.attribute<string>('orellthalia_communityName');
		items.add(
			'separating-phrase',
			<p>{app.translator.trans('glowingblue-orell-thalia-common.forum.login.separating_phrase_login', { communityName })}</p>,
			100
		);
	});

	extend(UserSecurityPage.prototype, 'settingsItems', function (items) {
		if (this.user !== app.session.user) {
			return;
		}

		// We want our custom version in the settings
		items.remove('linkedAccounts');
	});

	extend(SettingsPage.prototype, 'accountItems', function (items) {
		if (this.user !== app.session.user) {
			return;
		}

		items.add('linkedAccounts', <LinkedAccounts user={this.user} />, -100);
	});

	extend(SettingsPage.prototype, 'settingsItems', function (items) {
		// Reinsert the account item with a priority so that we can insert our item right after this one
		items.setPriority('account', 101);

		// Check if user has already connected his accounts (flarum & online shop)
		if (this.user?.linkedShop()) {
			// Remove the legend of the account item, if it is still present and is a vnode
			if (items.has('account')) {
				const accountItem = items.get('account');
				if (isVnode(accountItem)) {
					accountItem.attrs.label = '';
				}
			}
			const isOrell = app.forum.attribute<boolean>('isOrell');
			const brandName = app.forum.attribute<string>('orellthalia_brandName');

			let shopUrl;
			if (isOrell === true) {
				shopUrl = 'https://www.orellfuessli.ch/shop/home/kunde/';
			} else {
				shopUrl = 'https://www.thalia.de/shop/home/kunde/';
			}
			items.add(
				'of-settings',
				<div>
					<legend>{app.translator.trans('glowingblue-orell-thalia-common.forum.user.of_settings_header', { brandName })}</legend>
					<LinkButton href={shopUrl} className="LinkButton" target="_blank">
						{app.translator.trans('core.forum.header.settings_button')}
					</LinkButton>
				</div>,
				110
			);
		}
	});

	extend(SettingsPage.prototype, 'accountItems', function (items) {
		if (this.user?.linkedShop()) {
			items.remove('changePassword');
			items.remove('changeEmail');
		}
	});

	extend(SettingsPage.prototype, 'view', function (vnode) {
		// if linked, add a css class to the top level element
		if (this.user?.linkedShop()) {
			// @ts-ignore
			vnode.attrs.className = `${vnode.attrs.className || ''} oft-linked`;
		} else {
			// @ts-ignore
			vnode.attrs.className = `${vnode.attrs.className || ''} oft-not-linked`;
		}
	});

	// @ts-ignore
	override(Application.prototype, 'requestErrorDefaultHandler', function (original, e, isDebug, formattedErrors) {
		// Define the regex for matching specific errors
		const specificErrorRegex = /(NotAuthenticatedException|orellfuessli|oauth)/i;

		if (e instanceof RequestError) {
			// Check if the error message matches the specific errors
			const isSpecificError = specificErrorRegex.test(e.responseText || '');

			// If it's a specific error, we skip the logging
			if (!isSpecificError) {
				// console.log('Original error handler');
				original(e, isDebug, formattedErrors);
			} else {
				// console.log('Skipping logging of specific error');
				throw e;
			}
		} else {
			// console.log('Original error handler (else)');
			original(e, isDebug, formattedErrors);
		}
	});
}
